import { CAKE_URL, MAPBOX_KEY } from 'components/globals';
import type { HubSellingTransaction } from 'components/hub/useHubData';
import { buildPropertySpecsLine } from 'components/marketplace/property/helpers';

import type { TransactionCardModel } from './TransactionCard';

const SALE_PROGRESS_SEGMENTS = 6;

type DocumentTask = { complete: boolean };

interface SaleTaskSummary {
  statusLabel: string;
  footerMessage: string;
  ctaLabel: string;
}

const hasIncompleteDocumentTask = (task: DocumentTask | null | undefined): boolean =>
  Boolean(task && !task.complete);

const hasIncompleteDocumentTasks = (tasks: DocumentTask[] | null | undefined): boolean =>
  Boolean(tasks?.some((task) => !task.complete));

/**
 * Minimal fields needed to classify a sale transaction's lifecycle. Accepting a
 * structural subset rather than the full `HubSellingTransaction` lets lean
 * callers (e.g. the nav menu) reuse this classification without fetching the
 * entire Hub payload.
 */
export type SaleTransactionStatusFields = Pick<HubSellingTransaction, '__typename'> & {
  sellDirectOffer?: { isClosed?: boolean | null } | null;
};

export interface SaleTransactionHrefFields {
  address?: { uuid?: string | null } | null;
  sellerInput?: { uuid?: string | null } | null;
}

const isClosedInContractTransaction = (transaction: SaleTransactionStatusFields): boolean =>
  transaction.__typename === 'InContractTransaction' &&
  transaction.sellDirectOffer?.isClosed === true;

export const isActiveSaleTransaction = (transaction: SaleTransactionStatusFields): boolean =>
  transaction.__typename === 'InitializingTransaction' ||
  transaction.__typename === 'QualifiedTransaction' ||
  (transaction.__typename === 'InContractTransaction' &&
    !isClosedInContractTransaction(transaction));

export const isPastSaleTransaction = (transaction: SaleTransactionStatusFields): boolean =>
  transaction.__typename === 'DeniedTransaction' ||
  transaction.__typename === 'WithdrawnTransaction' ||
  isClosedInContractTransaction(transaction);

const buildAddressLine = (transaction: HubSellingTransaction): string => {
  const { address } = transaction;
  const streetLine = [address.street, address.unitNumber].filter(Boolean).join(' ');
  const cityState = [address.city, address.state].filter(Boolean).join(', ');

  return [streetLine, cityState, address.postalCode].filter(Boolean).join(' ') || 'Your home sale';
};

const buildMapPreviewUrl = (transaction: HubSellingTransaction): string | null => {
  const { latitude, longitude } = transaction.address;

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  const lat = latitude.toFixed(5);
  const lon = longitude.toFixed(5);
  // `logo=false&attribution=false` drop Mapbox's baked-in watermark/attribution
  // from this thumbnail. Mapbox attribution is still surfaced elsewhere (the
  // interactive marketplace maps), satisfying their attribution requirement.
  return `https://api.mapbox.com/styles/v1/mapbox/light-v11/static/pin-s+222222(${lon},${lat})/${lon},${lat},14,0/320x200@2x?access_token=${MAPBOX_KEY}&logo=false&attribution=false`;
};

export const buildSaleHref = (transaction: SaleTransactionHrefFields): string => {
  if (transaction.address?.uuid) {
    return `${CAKE_URL}/dashboard/sell/${transaction.address.uuid}`;
  }
  if (transaction.sellerInput?.uuid) {
    return `${CAKE_URL}/dashboard/${transaction.sellerInput.uuid}/overview`;
  }
  return `${CAKE_URL}/dashboard/sell`;
};

const buildDetailsLine = (transaction: HubSellingTransaction): string | null => {
  const physicalDetails = transaction.sellerInput?.homeDetails?.physicalDetails;
  if (!physicalDetails) {
    return null;
  }

  const bathrooms =
    physicalDetails.bathrooms ??
    (physicalDetails.bathroomsFull != null || physicalDetails.bathroomsHalf != null
      ? (physicalDetails.bathroomsFull ?? 0) + (physicalDetails.bathroomsHalf ?? 0) / 2
      : null);

  return buildPropertySpecsLine(physicalDetails.bedrooms, bathrooms, physicalDetails.sqFt);
};

type AssessmentStep = Extract<
  NonNullable<
    Extract<HubSellingTransaction, { __typename?: 'QualifiedTransaction' }>['currentStep']
  >,
  { __typename?: 'AssessmentStep' }
>;

type SelfServeVirtualAssessmentData = NonNullable<
  NonNullable<AssessmentStep['selfServeVirtualAssessment']>['data']
>;

const isSelfServeVirtualAssessmentComplete = (
  data: SelfServeVirtualAssessmentData | null | undefined,
): boolean =>
  Boolean(
    data?.ssvaCompletedAt || (data?.ssvaRecordingsSubmitted && data?.ssvaQuestionnaireSubmitted),
  );

const hasSelfServeVirtualAssessmentProgress = (
  data: SelfServeVirtualAssessmentData | null | undefined,
): boolean =>
  Boolean(
    data?.ssvaRecordingsSubmitted ||
    data?.ssvaQuestionnaireSubmitted ||
    data?.ssvaInteriorRecordingsSubmitted ||
    data?.ssvaBackyardRecordingsSubmitted ||
    data?.ssvaFrontyardRecordingsSubmitted ||
    data?.ssvaWholeHomeRecordingsSubmitted ||
    data?.ssvaSellerDisclosureDocumentRecordingsSubmitted,
  );

const isAssessmentComplete = (step: AssessmentStep): boolean =>
  Boolean(
    step.appAssessment?.completedAt ||
    isSelfServeVirtualAssessmentComplete(step.selfServeVirtualAssessment?.data),
  );

const buildAssessmentSummary = (step: AssessmentStep): SaleTaskSummary => {
  const selfServeVirtualAssessmentData = step.selfServeVirtualAssessment?.data;

  if (isAssessmentComplete(step)) {
    return {
      statusLabel: 'Review offer',
      footerMessage: 'Your home assessment is complete. View your home sale for the next step',
      ctaLabel: 'View task',
    };
  }

  if (hasSelfServeVirtualAssessmentProgress(selfServeVirtualAssessmentData)) {
    return {
      statusLabel: 'Assessment in progress',
      footerMessage: 'Finish your home assessment to continue with the home sale',
      ctaLabel: 'View task',
    };
  }

  if (step.inPersonAssessment?.isScheduled || step.expertHandoffAssessment?.isScheduled) {
    return {
      statusLabel: 'Assessment scheduled',
      footerMessage: 'Your home assessment is scheduled. View your home sale for details',
      ctaLabel: 'View task',
    };
  }

  return {
    statusLabel: 'Schedule inspection',
    footerMessage: 'Schedule an inspection with our agent to continue with the home sale',
    ctaLabel: 'View task',
  };
};

const buildSigningSummary = (
  step: Extract<
    NonNullable<
      Extract<
        HubSellingTransaction,
        { __typename?: 'QualifiedTransaction' | 'InContractTransaction' }
      >['currentStep']
    >,
    { __typename?: 'ContractSigningStep' | 'RepricingStep' }
  >,
): SaleTaskSummary => {
  if (step.contractSetup && !step.contractSetup.complete) {
    return {
      statusLabel: 'Complete setup',
      footerMessage: 'Finish your contract setup to continue with the home sale',
      ctaLabel: 'View task',
    };
  }

  if (step.purchaseAgreement && !step.purchaseAgreement.complete) {
    return {
      statusLabel: 'Sign contract',
      footerMessage: 'Sign your contract to continue with the home sale',
      ctaLabel: 'View task',
    };
  }

  return {
    statusLabel: 'Review contract',
    footerMessage: 'View your contract details and next steps',
    ctaLabel: 'View task',
  };
};

const buildClosingSummary = (
  step: Extract<
    NonNullable<
      Extract<HubSellingTransaction, { __typename?: 'InContractTransaction' }>['currentStep']
    >,
    { __typename?: 'ClosingStep' }
  >,
): SaleTaskSummary => {
  const { requiredTasks } = step;
  const { inspection } = requiredTasks;

  if (inspection && !inspection.complete) {
    if (inspection.state === 'NOT_SCHEDULED') {
      return {
        statusLabel: 'Schedule inspection',
        footerMessage: 'Schedule an inspection with our agent to continue with the home sale',
        ctaLabel: 'View task',
      };
    }

    return {
      statusLabel: 'Inspection in progress',
      footerMessage: 'Your inspection is underway. View your home sale for details',
      ctaLabel: 'View task',
    };
  }

  if (requiredTasks.disclosures && !requiredTasks.disclosures.complete) {
    return {
      statusLabel: 'Sign disclosures',
      footerMessage: 'Complete your disclosures to keep the home sale moving',
      ctaLabel: 'View task',
    };
  }

  if (requiredTasks.serviceProviders && !requiredTasks.serviceProviders.complete) {
    return {
      statusLabel: 'Complete home info',
      footerMessage: 'Add your home information to keep the home sale moving',
      ctaLabel: 'View task',
    };
  }

  if (requiredTasks.finalWalkthrough && !requiredTasks.finalWalkthrough.complete) {
    return {
      statusLabel: 'Complete checkout',
      footerMessage: 'Complete your final walkthrough before closing',
      ctaLabel: 'View task',
    };
  }

  if (
    hasIncompleteDocumentTask(requiredTasks.lateCheckoutAddendum) ||
    hasIncompleteDocumentTask(requiredTasks.changeCloseOfEscrowAddendum) ||
    hasIncompleteDocumentTasks(requiredTasks.formAddendums)
  ) {
    return {
      statusLabel: 'Sign addendum',
      footerMessage: 'Sign your addendum to keep the home sale moving',
      ctaLabel: 'View task',
    };
  }

  return {
    statusLabel: 'View progress',
    footerMessage: 'Keep your closing on track in your home sale dashboard',
    ctaLabel: 'View task',
  };
};

const buildTaskSummary = (transaction: HubSellingTransaction): SaleTaskSummary => {
  if (transaction.__typename === 'InitializingTransaction') {
    return {
      statusLabel: 'Preparing offer',
      footerMessage: 'We’re preparing your home sale details',
      ctaLabel: 'View task',
    };
  }

  if (transaction.__typename === 'QualifiedTransaction') {
    const step = transaction.currentStep;
    switch (step?.__typename) {
      case 'AgentAttributionDecisionStep':
        return {
          statusLabel: 'Review agent',
          footerMessage: 'Review your agent details to continue with the home sale',
          ctaLabel: 'View task',
        };
      case 'ReferralInvitationDecisionStep':
        return {
          statusLabel: 'Review referral',
          footerMessage: 'Review your referral invitation to continue with the home sale',
          ctaLabel: 'View task',
        };
      case 'AssessmentStep':
        return buildAssessmentSummary(step);
      case 'HoldingOfferStep':
        return {
          statusLabel: 'Offer in progress',
          footerMessage: 'Your offer is being prepared. View your home sale for updates',
          ctaLabel: 'View task',
        };
      case 'UnderwritingStep':
        return {
          statusLabel: 'Finalizing offer',
          footerMessage: 'We’re finalizing your offer. View your home sale for updates',
          ctaLabel: 'View task',
        };
      case 'ContractSigningStep':
        return buildSigningSummary(step);
      default:
        return {
          statusLabel: 'View progress',
          footerMessage: 'View your home sale tasks and next steps',
          ctaLabel: 'View task',
        };
    }
  }

  if (transaction.__typename === 'InContractTransaction') {
    const step = transaction.currentStep;
    switch (step?.__typename) {
      case 'ClosingStep':
        return buildClosingSummary(step);
      case 'RepricingStep':
        return buildSigningSummary(step);
      default:
        return {
          statusLabel: 'View progress',
          footerMessage: 'View your home sale tasks and next steps',
          ctaLabel: 'View task',
        };
    }
  }

  return {
    statusLabel: 'View progress',
    footerMessage: 'View your home sale details',
    ctaLabel: 'View sale',
  };
};

const getSaleCompletedSegments = (transaction: HubSellingTransaction): number => {
  if (transaction.__typename === 'InitializingTransaction') {
    return 1;
  }

  if (transaction.__typename === 'QualifiedTransaction') {
    switch (transaction.currentStep?.__typename) {
      case 'AgentAttributionDecisionStep':
      case 'ReferralInvitationDecisionStep':
        return 1;
      case 'AssessmentStep':
      case 'HoldingOfferStep':
        return 2;
      case 'UnderwritingStep':
        return 3;
      case 'ContractSigningStep':
        return 4;
      default:
        return 1;
    }
  }

  if (transaction.__typename === 'InContractTransaction') {
    return 5;
  }

  return 0;
};

const getPastSaleStatusLabel = (
  transaction: HubSellingTransaction,
  isCompletedSale: boolean,
): string => {
  if (isCompletedSale) {
    return 'Sale completed';
  }

  if (transaction.__typename === 'WithdrawnTransaction') {
    return 'Offer withdrawn';
  }

  return 'Past offer';
};

export const buildSaleTransactionCardModel = (
  transaction: HubSellingTransaction,
): TransactionCardModel | null => {
  if (!isActiveSaleTransaction(transaction)) {
    return null;
  }

  const taskSummary = buildTaskSummary(transaction);

  return {
    id: transaction.id,
    href: buildSaleHref(transaction),
    media: { kind: 'map', url: buildMapPreviewUrl(transaction) },
    addressLine: buildAddressLine(transaction),
    priceLabel: null,
    detailsLine: buildDetailsLine(transaction),
    isMuted: false,
    totalSegments: SALE_PROGRESS_SEGMENTS,
    completedSegments: getSaleCompletedSegments(transaction),
    statusLabel: taskSummary.statusLabel,
    footerMessage: taskSummary.footerMessage,
    ctaLabel: taskSummary.ctaLabel,
    ctaAnalyticsName: 'hub-sale-view-task',
    ctaVariant: 'primary',
  };
};

export const buildPastSaleTransactionCardModel = (
  transaction: HubSellingTransaction,
): TransactionCardModel | null => {
  if (!isPastSaleTransaction(transaction)) {
    return null;
  }

  const isCompletedSale = isClosedInContractTransaction(transaction);

  return {
    id: transaction.id,
    href: buildSaleHref(transaction),
    media: { kind: 'map', url: buildMapPreviewUrl(transaction) },
    addressLine: buildAddressLine(transaction),
    priceLabel: null,
    detailsLine: buildDetailsLine(transaction),
    isMuted: true,
    totalSegments: SALE_PROGRESS_SEGMENTS,
    completedSegments: SALE_PROGRESS_SEGMENTS,
    statusLabel: getPastSaleStatusLabel(transaction, isCompletedSale),
    footerMessage: isCompletedSale
      ? 'This home sale is complete'
      : 'This home sale offer is no longer active',
    ctaLabel: 'View details',
    ctaAnalyticsName: 'hub-sale-view-details',
    ctaVariant: 'secondary',
  };
};
